/**
 * Hook for server-side Deals grid data
 *
 * Fetches data from the in-memory search index with pagination, sorting, and filtering.
 * Designed to work with MUI Data Grid Premium server-side mode.
 *
 * Uses React Query for cache management and invalidation.
 * Invalidate with: queryClient.invalidateQueries({ queryKey: ['search', 'deals'] })
 */

import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://grobotic.app:2096'

/** Query key for deals search - use for invalidation */
export const DEALS_SEARCH_KEY = ['search', 'deals'] as const

interface SearchParams {
  page: number
  pageSize: number
  sortModel?: GridSortModel
  filterModel?: GridFilterModel
}

interface SearchResult {
  rows: any[]
  totalCount: number
  page: number
  pageSize: number
  aggregations?: {
    total: number
    grossProfit: number
    mediaSpend: number
    implementationFee: number
  }
}

interface UseServerSideDealsOptions {
  enabled?: boolean
  pageSize?: number
  initialSortModel?: GridSortModel
  initialFilterModel?: GridFilterModel
}

interface UseServerSideDealsReturn {
  // Data
  rows: any[]
  totalRowCount: number
  aggregations?: SearchResult['aggregations']

  // Pagination
  page: number
  pageSize: number
  onPageChange: (newPage: number) => void
  onPageSizeChange: (newPageSize: number) => void

  // Sorting
  sortModel: GridSortModel
  onSortModelChange: (newSortModel: GridSortModel) => void

  // Filtering
  filterModel: GridFilterModel
  onFilterModelChange: (newFilterModel: GridFilterModel) => void

  // Loading state
  isLoading: boolean
  error: string | null

  // Refresh
  refresh: () => Promise<void>

  // Server-side mode props for DataGridPremium
  serverSideProps: {
    paginationMode: 'server'
    sortingMode: 'server'
    filterMode: 'server'
    rowCount: number
    paginationModel: { page: number; pageSize: number }
    onPaginationModelChange: (model: { page: number; pageSize: number }) => void
    loading: boolean
  }
}

/**
 * Fetch function for the deals search API
 */
const fetchDealsSearch = async (params: SearchParams): Promise<SearchResult> => {
  const response = await fetch(`${API_BASE}/search/deals`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Hook for fetching deals with server-side pagination, sorting, and filtering
 */
export const useServerSideDeals = (options: UseServerSideDealsOptions = {}): UseServerSideDealsReturn => {
  const {
    enabled = true,
    pageSize: initialPageSize = 50,
    initialSortModel = [{ field: 'name', sort: 'asc' }],
    initialFilterModel = { items: [], quickFilterValues: [] },
  } = options

  const queryClient = useQueryClient()

  // Controlled state for grid parameters
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel)
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel)

  // Debounced filter model for the query
  const [debouncedFilterModel, setDebouncedFilterModel] = useState<GridFilterModel>(initialFilterModel)
  const filterDebounceRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Debounce filter changes
  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current)
    }

    filterDebounceRef.current = setTimeout(() => {
      setDebouncedFilterModel(filterModel)
      setPage(0)
    }, 300)

    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current)
      }
    }
  }, [filterModel])

  // Build query key with all search parameters
  const queryKey = [...DEALS_SEARCH_KEY, { page, pageSize, sortModel, filterModel: debouncedFilterModel }] as const

  // Use React Query for data fetching
  const {
    data,
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: () =>
      fetchDealsSearch({
        page,
        pageSize,
        sortModel,
        filterModel: debouncedFilterModel,
      }),
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    placeholderData: previousData => previousData,
  })

  // Extract data from query result
  const rows = data?.rows ?? []
  const totalRowCount = data?.totalCount ?? 0
  const aggregations = data?.aggregations

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const onPageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
  }, [])

  const onSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel)
    setPage(0)
  }, [])

  const onFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel)
  }, [])

  const onPaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    setPage(model.page)
    setPageSize(model.pageSize)
  }, [])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: DEALS_SEARCH_KEY })
  }, [queryClient])

  // Props to spread onto DataGridPremium for server-side mode
  const serverSideProps = {
    paginationMode: 'server' as const,
    sortingMode: 'server' as const,
    filterMode: 'server' as const,
    rowCount: totalRowCount,
    paginationModel: { page, pageSize },
    onPaginationModelChange,
    loading: isLoading || isFetching,
  }

  return {
    rows,
    totalRowCount,
    aggregations,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    isLoading,
    error: queryError?.message ?? null,
    refresh,
    serverSideProps,
  }
}

/**
 * Check if server-side deals search is available
 */
export const checkDealsSearchHealth = async (): Promise<{
  available: boolean
  message: string
  documentCount?: number
  lastSyncTime?: string
  isInitialized?: boolean
}> => {
  try {
    const response = await fetch(`${API_BASE}/search/health`)
    const data = await response.json()

    return {
      available: data.healthy && data.deals?.ready,
      message: data.deals?.ready ? 'Deals search available' : 'Deals search unavailable',
      documentCount: data.deals?.documentCount,
      lastSyncTime: data.deals?.lastSyncTime,
      isInitialized: data.deals?.isInitialized,
    }
  } catch (err) {
    return {
      available: false,
      message: 'Search service not reachable',
    }
  }
}

/**
 * Trigger a full deals sync (admin only)
 */
export const triggerDealsFullSync = async (): Promise<{ success: boolean; indexed?: number; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/search/deals/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, error: data.message || 'Sync failed' }
    }

    return { success: true, indexed: data.indexed }
  } catch (err) {
    return { success: false, error: err.message }
  }
}

export default useServerSideDeals
